import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";

const AuthContext = createContext(null);
const TOKEN_KEY = "welljio_auth_token";

// Attach token to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setUser(null); setChecking(false); return null; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setUser(null);
      return null;
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    localStorage.setItem(TOKEN_KEY, data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };


  // Permission check. Once the RBAC backend ships, /auth/me should return a
  // `permissions` array (e.g. ["orders.view", "orders.fulfil", ...]) alongside
  // `role`. Until then, `role === "admin"` is treated as having every
  // permission, matching today's hardcoded two-role behavior — this keeps
  // existing admin accounts working unchanged during the transition.
  const can = useCallback(
    (permission) => {
      if (!user) return false;
      if (Array.isArray(user.permissions)) return user.permissions.includes(permission);
      return user.role === "admin";
    },
    [user]
  );
  
  // "Is this a staff account at all" — any role other than the public
  // "customer" role. Used for coarse gating (e.g. can this account even open
  // /admin), separate from fine-grained can() checks for specific actions.
  const isStaff = !!user && user.role !== "customer";
  return (
    <AuthContext.Provider value={{ user, checking, login, register, logout, refresh: fetchMe, can, isStaff }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
